/**
 * Copyright (c) 2017-2020 Mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __assign } from "tslib";
import { IntraUnitBonds } from './data';
import { IntAdjacencyGraph } from '../../../../../mol-math/graph';
import { getElementIdx, MetalsSet, getElementThreshold, isHydrogen, getElementPairThreshold, DefaultBondComputationProps } from './common';
import { SortedArray } from '../../../../../mol-data/int';
import { getIntraBondOrderFromTable } from '../../../model/properties/atomic/bonds';
import { IndexPairBonds } from '../../../../../mol-model-formats/structure/property/bonds/index-pair';
import { ComponentBond } from '../../../../../mol-model-formats/structure/property/bonds/chem_comp';
import { StructConn } from '../../../../../mol-model-formats/structure/property/bonds/struct_conn';
import { Vec3 } from '../../../../../mol-math/linear-algebra';
import { equalEps } from '../../../../../mol-math/linear-algebra/3d/common';
import { Model } from '../../../model/model';

function getGraph(atomA, atomB, _order, _flags, atomCount, canRemap) {
  var builder = new IntAdjacencyGraph.EdgeBuilder(atomCount, atomA, atomB);
  var flags = new Uint16Array(builder.slotCount);
  var order = new Int8Array(builder.slotCount);

  for (var i = 0, _i = builder.edgeCount; i < _i; i++) {
    builder.addNextEdge();
    builder.assignProperty(flags, _flags[i]);
    builder.assignProperty(order, _order[i]);
  }

  return builder.createGraph({
    flags: flags,
    order: order
  }, {
    canRemap: canRemap
  });
}

var tmpDistVecA = Vec3();
var tmpDistVecB = Vec3();

function getDistance(unit, indexA, indexB) {
  unit.conformation.position(indexA, tmpDistVecA);
  unit.conformation.position(indexB, tmpDistVecB);
  return Vec3.distance(tmpDistVecA, tmpDistVecB);
}

var __structConnAdded = new Set();

function findIndexPairBonds(unit) {
  var indexPairs = IndexPairBonds.Provider.get(unit.model);
  var atoms = unit.elements;
  var type_symbol = unit.model.atomicHierarchy.atoms.type_symbol;
  var atomCount = unit.elements.length;
  var edgeProps = indexPairs.edgeProps;
  var sourceIndex = unit.model.atomicHierarchy.atomSourceIndex;
  var invertedIndex = Model.getInvertedAtomSourceIndex(unit.model).invertedIndex;
  var atomA = [];
  var atomB = [];
  var flags = [];
  var order = [];

  for (var _aI = 0; _aI < atomCount; _aI++) {
    var aI = atoms[_aI];
    var isHa = type_symbol.value(aI) === 'H';
    var srcA = sourceIndex.value(aI);

    for (var i = indexPairs.offset[srcA], il = indexPairs.offset[srcA + 1]; i < il; ++i) {
      var bI = invertedIndex[indexPairs.b[i]];
      if (aI >= bI) continue;

      var _bI = SortedArray.indexOf(unit.elements, bI);

      if (_bI < 0) continue;
      if (isHa && type_symbol.value(bI) === 'H') continue;
      var d = edgeProps.distance[i];

      if (d === -1 || d === void 0 || equalEps(getDistance(unit, aI, bI), d, 0.5)) {
        atomA[atomA.length] = _aI;
        atomB[atomB.length] = _bI;
        order[order.length] = edgeProps.order[i];
        flags[flags.length] = edgeProps.flag[i];
      }
    }
  }

  return getGraph(atomA, atomB, order, flags, atomCount, false);
}

function findBonds(unit, props) {
  var MAX_RADIUS = 4;
  var _a = unit.model.atomicConformation,
      x = _a.x,
      y = _a.y,
      z = _a.z;
  var atomCount = unit.elements.length;
  var atoms = unit.elements,
      residueIndex = unit.residueIndex,
      chainIndex = unit.chainIndex;
  var _b = unit.model.atomicHierarchy.atoms,
      type_symbol = _b.type_symbol,
      label_atom_id = _b.label_atom_id,
      label_alt_id = _b.label_alt_id,
      label_comp_id = _b.label_comp_id;
  var label_seq_id = unit.model.atomicHierarchy.residues.label_seq_id;
  var index = unit.model.atomicHierarchy.index;
  var byEntityKey = unit.model.sequence.byEntityKey;
  var query3d = unit.lookup3d;
  var structConn = StructConn.Provider.get(unit.model);
  var component = ComponentBond.Provider.get(unit.model);
  var structConnExhaustive = StructConn.isExhaustive(unit.model);
  var atomA = [];
  var atomB = [];
  var flags = [];
  var order = [];
  var lastResidue = -1;
  var componentMap = void 0;
  var isWatery = true,
      isDictionaryBased = true,
      isSequenced = true;
  var structConnAdded = __structConnAdded;

  for (var _aI = 0; _aI < atomCount; _aI++) {
    var aI = atoms[_aI];
    var elemA = type_symbol.value(aI);
    if (isWatery && (elemA !== 'H' || elemA !== 'O')) isWatery = false;
    var structConnEntries = props.forceCompute ? void 0 : structConn && structConn.byAtomIndex.get(aI);
    var hasStructConn = false;

    if (structConnEntries) {
      for (var _c = 0, structConnEntries_1 = structConnEntries; _c < structConnEntries_1.length; _c++) {
        var se = structConnEntries_1[_c];
        var partnerA = se.partnerA,
            partnerB = se.partnerB; // symmetry must be the same for intra-unit bonds

        if (partnerA.symmetry !== partnerB.symmetry) continue;
        var p = partnerA.atomIndex === aI ? partnerB : partnerA;

        var _bI = SortedArray.indexOf(unit.elements, p.atomIndex);

        if (_bI < 0) continue;
        atomA[atomA.length] = _aI;
        atomB[atomB.length] = _bI;
        flags[flags.length] = se.flags;
        order[order.length] = se.order;
        if (!hasStructConn) structConnAdded.clear();
        hasStructConn = true;
        structConnAdded.add(_bI);
      }
    }

    if (structConnExhaustive) continue;
    var raI = residueIndex[aI];
    var seqIdA = label_seq_id.value(raI);
    var compId = label_comp_id.value(aI);

    if (!props.forceCompute && raI !== lastResidue) {
      if (!!component && component.entries.has(compId)) {
        var entitySeq = byEntityKey[index.getEntityFromChain(chainIndex[aI])];

        if (entitySeq && entitySeq.sequence.microHet.has(seqIdA)) {
          // compute for sequence positions with micro-heterogeneity
          componentMap = void 0;
        } else {
          componentMap = component.entries.get(compId).map;
        }
      } else {
        componentMap = void 0;
      }
    }

    lastResidue = raI;
    var aeI = getElementIdx(elemA);
    var atomIdA = label_atom_id.value(aI);
    var componentPairs = componentMap ? componentMap.get(atomIdA) : void 0;

    var _d = query3d.find(x[aI], y[aI], z[aI], MAX_RADIUS),
        indices = _d.indices,
        count = _d.count,
        squaredDistances = _d.squaredDistances;

    var isHa = isHydrogen(aeI);
    var thresholdA = getElementThreshold(aeI);
    var altA = label_alt_id.value(aI);
    var metalA = MetalsSet.has(aeI);

    for (var ni = 0; ni < count; ni++) {
      var _bI = indices[ni];
      if (hasStructConn && structConnAdded.has(_bI)) continue;
      var bI = atoms[_bI];
      if (bI <= aI) continue;
      var altB = label_alt_id.value(bI);
      if (altA && altB && altA !== altB) continue;
      var beI = getElementIdx(type_symbol.value(bI));
      var isHb = isHydrogen(beI);
      if (isHa && isHb) continue;
      var isMetal = (metalA || MetalsSet.has(beI)) && !(isHa || isHb);
      var rbI = residueIndex[bI]; // handle "component dictionary" bonds.

      if (raI === rbI && componentPairs) {
        var e = componentPairs.get(label_atom_id.value(bI));

        if (e) {
          atomA[atomA.length] = _aI;
          atomB[atomB.length] = _bI;
          order[order.length] = e.order;
          var flag = e.flags;

          if (isMetal) {
            if (flag | 1
            /* Covalent */
            ) flag ^= 1
              /* Covalent */
              ;
            flag |= 2
            /* MetallicCoordination */
            ;
          }

          flags[flags.length] = flag;
        }

        continue;
      }

      var dist = Math.sqrt(squaredDistances[ni]);
      if (dist === 0) continue;
      var thresholdAB = getElementPairThreshold(aeI, beI);
      var pairingThreshold = thresholdAB > 0 ? thresholdAB : beI < 0 ? thresholdA : (thresholdA + getElementThreshold(beI)) / 1.95; // not sure if avg or min but max is too big

      if (dist <= pairingThreshold) {
        atomA[atomA.length] = _aI;
        atomB[atomB.length] = _bI;
        order[order.length] = getIntraBondOrderFromTable(compId, atomIdA, label_atom_id.value(bI));
        flags[flags.length] = (isMetal ? 2
        /* MetallicCoordination */
        : 1
        /* Covalent */
        ) | 32
        /* Computed */
        ;
        var seqIdB = label_seq_id.value(rbI);
        if (seqIdA === seqIdB) isDictionaryBased = false;
        if (Math.abs(seqIdA - seqIdB) > 1) isSequenced = false;
      }
    }
  }

  var canRemap = isWatery || isDictionaryBased && isSequenced;
  return getGraph(atomA, atomB, order, flags, atomCount, canRemap);
}

function computeIntraUnitBonds(unit, props) {
  var p = __assign(__assign({}, DefaultBondComputationProps), props);

  if (p.noCompute || Model.isCoarseGrained(unit.model)) {
    // TODO add function that only adds bonds defined in structConn of chemCompBond
    //      and avoid using unit.lookup
    return IntraUnitBonds.Empty;
  }

  if (!p.forceCompute && IndexPairBonds.Provider.get(unit.model)) {
    return findIndexPairBonds(unit);
  } else {
    return findBonds(unit, p);
  }
}

export { computeIntraUnitBonds };